/**
 * WebAuthn / Passkey helpers for Selah Slate.
 * Handles Face ID (iOS), Touch ID (macOS), Windows Hello, and Android biometric.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// --- base64url ↔ ArrayBuffer helpers (browsers ship no native ones) ---
function b64urlToBytes(s) {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bytesToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodePublicKeyOptions(opts) {
  const out = { ...opts };
  out.challenge = b64urlToBytes(out.challenge);
  if (out.user?.id) out.user = { ...out.user, id: b64urlToBytes(out.user.id) };
  if (out.excludeCredentials) {
    out.excludeCredentials = out.excludeCredentials.map((c) => ({ ...c, id: b64urlToBytes(c.id) }));
  }
  if (out.allowCredentials) {
    out.allowCredentials = out.allowCredentials.map((c) => ({ ...c, id: b64urlToBytes(c.id) }));
  }
  return out;
}

function serializeCredential(cred) {
  const r = cred.response;
  const out = {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: { clientDataJSON: bytesToB64url(r.clientDataJSON) },
  };
  if (r.attestationObject) {
    out.response.attestationObject = bytesToB64url(r.attestationObject);
    if (r.getTransports) out.response.transports = r.getTransports();
  } else {
    out.response.authenticatorData = bytesToB64url(r.authenticatorData);
    out.response.signature = bytesToB64url(r.signature);
    if (r.userHandle) out.response.userHandle = bytesToB64url(r.userHandle);
  }
  if (cred.authenticatorAttachment) out.authenticatorAttachment = cred.authenticatorAttachment;
  return out;
}

/**
 * Feature detection: is a platform authenticator (Face ID, Touch ID, Windows Hello,
 * Android fingerprint) available on this device?
 */
export async function isPlatformPasskeyAvailable() {
  if (typeof window === 'undefined') return false;
  if (!window.PublicKeyCredential) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Check whether the given email has any passkeys registered on the server.
 */
export async function emailHasPasskey(email) {
  try {
    const { data } = await axios.get(`${API_URL}/api/webauthn/status`, {
      params: { email },
    });
    return !!data?.has_passkey;
  } catch {
    return false;
  }
}

/**
 * Register a new passkey for the currently logged-in user.
 * Assumes the axios interceptor is attaching the JWT bearer.
 */
export async function registerPasskey({ deviceName } = {}) {
  const { data: begin } = await axios.post(`${API_URL}/api/webauthn/register/begin`, {});
  const publicKey = decodePublicKeyOptions(begin.options);
  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) throw new Error('No credential returned');
  const payload = {
    challenge_id: begin.challenge_id,
    credential: serializeCredential(credential),
    device_name: deviceName || guessDeviceName(),
  };
  const { data: done } = await axios.post(`${API_URL}/api/webauthn/register/complete`, payload);
  return done;
}

/**
 * Log in with a passkey for the given email.
 * Returns the same shape as /api/auth/login on success.
 */
export async function loginWithPasskey(email) {
  const { data: begin } = await axios.post(`${API_URL}/api/webauthn/login/begin`, { email });
  const publicKey = decodePublicKeyOptions(begin.options);
  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error('No assertion returned');
  const payload = {
    challenge_id: begin.challenge_id,
    credential: serializeCredential(assertion),
  };
  const { data: done } = await axios.post(`${API_URL}/api/webauthn/login/complete`, payload);
  return done;
}

export async function listPasskeys() {
  const { data } = await axios.get(`${API_URL}/api/webauthn/credentials`);
  return data?.credentials || [];
}

export async function deletePasskey(credentialId) {
  await axios.delete(`${API_URL}/api/webauthn/credentials/${encodeURIComponent(credentialId)}`);
}

export async function renamePasskey(credentialId, name) {
  const { data } = await axios.patch(
    `${API_URL}/api/webauthn/credentials/${encodeURIComponent(credentialId)}`,
    { name },
  );
  return data;
}

// --- localStorage flags to drive UX ---
const KEY_HINT = 'selah_last_passkey_email';
const KEY_PROMPTED = 'selah_passkey_prompted';

export function rememberPasskeyEmail(email) {
  if (!email) return;
  localStorage.setItem(KEY_HINT, email.toLowerCase());
}

export function getRememberedPasskeyEmail() {
  return localStorage.getItem(KEY_HINT) || '';
}

export function markEnrollmentPrompted(email) {
  if (!email) return;
  const set = new Set(JSON.parse(localStorage.getItem(KEY_PROMPTED) || '[]'));
  set.add(email.toLowerCase());
  localStorage.setItem(KEY_PROMPTED, JSON.stringify([...set]));
}

export function wasEnrollmentPrompted(email) {
  if (!email) return false;
  try {
    const arr = JSON.parse(localStorage.getItem(KEY_PROMPTED) || '[]');
    return arr.includes(email.toLowerCase());
  } catch {
    return false;
  }
}

/** Best-effort friendly device label from the user agent. */
export function guessDeviceName() {
  const ua = navigator.userAgent || '';
  if (/iPhone/i.test(ua)) return 'iPhone · Face ID';
  if (/iPad/i.test(ua)) return 'iPad · Face ID / Touch ID';
  if (/Mac OS X/i.test(ua) && /Safari/i.test(ua)) return 'Mac · Touch ID';
  if (/Android/i.test(ua)) return 'Android · Fingerprint';
  if (/Windows/i.test(ua)) return 'Windows · Hello';
  return 'This device';
}
